import { motion, useReducedMotion } from "framer-motion";
import { ArrowLeftRight, Award, Coins, Globe, Repeat, Wallet } from "lucide-react";
import { useLanguage } from "../../i18n/LanguageContext";
import { Reveal } from "./Reveal";
import { SectionHeader } from "./SectionHeader";

const ITEMS = [
    { icon: Globe, title: "Nexora Global" },
    { icon: ArrowLeftRight, title: "Token Trade" },
    { icon: Award, title: "Gold Global Program" },
    { icon: Coins, title: "AFG Token" },
    { icon: Repeat, title: "AFG DEX Listing" },
    { icon: Wallet, title: "Nexora Wallet" },
];

const LinePulse = ({ vertical = false }) => {
    const reduce = useReducedMotion();
    if (reduce) return null;
    return (
        <motion.span
            aria-hidden="true"
            className="absolute rounded-full bg-nx-lime shadow-[0_0_14px_rgba(184,255,24,0.75)]"
            style={vertical ? { left: 23.25, top: 0, width: 5, height: 5 } : { top: 22.5, left: 0, width: 6, height: 6 }}
            animate={vertical ? { top: ["0%", "100%"] } : { left: ["0%", "100%"] }}
            transition={{ duration: 9, repeat: Infinity, ease: "linear" }}
        />
    );
};

const Ease = [0.22, 1, 0.36, 1];

export const Roadmap = () => {
    const { t } = useLanguage();
    const reduce = useReducedMotion();
    return (
        <section id="roadmap" data-testid="roadmap-section" className="relative overflow-hidden border-t border-white/5 bg-nx-alt py-20 sm:py-24 lg:py-32">
            <div className="pointer-events-none absolute left-1/2 top-0 h-64 w-[50rem] -translate-x-1/2 rounded-full bg-nx-blue/[0.05] blur-[110px]" aria-hidden="true" />
            <div className="relative mx-auto max-w-7xl px-5 sm:px-6 lg:px-8">
                <SectionHeader
                    label={t("Future Ecosystem")}
                    testId="roadmap"
                    title={<>{t("The Next Chapter")}<br />{t("of Nexora.")}</>}
                    description={t("An expanding ecosystem designed to connect decentralized finance, digital assets and future Web3 infrastructure.")}
                />

                {/* Desktop horizontal timeline */}
                <div className="relative mt-20 hidden lg:block" data-testid="roadmap-timeline-desktop">
                    <div className="absolute left-0 right-0 top-6 h-px bg-white/5" aria-hidden="true" />
                    <motion.div
                        aria-hidden="true"
                        className="absolute left-0 top-6 h-px w-full origin-left bg-gradient-to-r from-nx-blue/60 via-nx-lime/60 to-nx-blue/60"
                        initial={reduce ? false : { scaleX: 0 }}
                        whileInView={{ scaleX: 1 }}
                        viewport={{ once: true, margin: "-80px" }}
                        transition={{ duration: 1.8, ease: Ease }}
                    />
                    <LinePulse />
                    <div className="grid grid-cols-6">
                        {ITEMS.map((item, i) => (
                            <div key={item.title} className="relative flex flex-col items-center px-2 text-center" data-testid={`roadmap-item-${i + 1}`}>
                                <motion.div
                                    initial={reduce ? false : { opacity: 0, scale: 0.6 }}
                                    whileInView={{ opacity: 1, scale: 1 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ delay: 0.35 + i * 0.18, duration: 0.5, ease: Ease }}
                                    className="relative z-10 flex h-12 w-12 items-center justify-center rounded-full border border-white/10 bg-nx-elev shadow-[0_8px_24px_rgba(0,0,0,0.5)]"
                                >
                                    <span className="absolute inset-0 rounded-full border border-nx-lime/20" aria-hidden="true" />
                                    <item.icon size={18} strokeWidth={1.5} className="text-nx-lime" aria-hidden="true" />
                                </motion.div>
                                <motion.div
                                    initial={reduce ? false : { opacity: 0, y: 14 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true, margin: "-80px" }}
                                    transition={{ delay: 0.45 + i * 0.18, duration: 0.55, ease: Ease }}
                                    className="mt-6"
                                >
                                    <span className="block text-[11px] font-extrabold text-nx-mute tabular">{String(i + 1).padStart(2, "0")}</span>
                                    <h3 className="mt-2 text-sm font-extrabold uppercase tracking-[0.08em] text-nx-text">{t(item.title)}</h3>
                                    <motion.span
                                        initial={reduce ? false : { opacity: 0 }}
                                        whileInView={{ opacity: 1 }}
                                        viewport={{ once: true, margin: "-80px" }}
                                        transition={{ delay: 0.6 + i * 0.18, duration: 0.5 }}
                                        className="mt-3 inline-block rounded-full border border-nx-lime/30 bg-nx-lime/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-nx-lime"
                                    >
                                        {t("Coming Soon")}
                                    </motion.span>
                                </motion.div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Mobile vertical timeline */}
                <div className="relative mt-12 lg:hidden" data-testid="roadmap-timeline-mobile">
                    <div className="absolute bottom-6 left-6 top-6 w-px bg-white/5" aria-hidden="true" />
                    <motion.div
                        aria-hidden="true"
                        className="absolute bottom-6 left-6 top-6 w-px origin-top bg-gradient-to-b from-nx-blue/60 via-nx-lime/60 to-nx-blue/60"
                        initial={reduce ? false : { scaleY: 0 }}
                        whileInView={{ scaleY: 1 }}
                        viewport={{ once: true, margin: "-60px" }}
                        transition={{ duration: 1.6, ease: Ease }}
                    />
                    <LinePulse vertical />
                    <div className="space-y-8">
                        {ITEMS.map((item, i) => (
                            <motion.div
                                key={item.title}
                                initial={reduce ? false : { opacity: 0, x: 16 }}
                                whileInView={{ opacity: 1, x: 0 }}
                                viewport={{ once: true, margin: "-40px" }}
                                transition={{ delay: i * 0.1, duration: 0.5, ease: Ease }}
                                className="relative flex items-center gap-5"
                                data-testid={`roadmap-mobile-item-${i + 1}`}
                            >
                                <div className="relative z-10 flex h-12 w-12 shrink-0 items-center justify-center rounded-full border border-white/10 bg-nx-elev shadow-[0_8px_24px_rgba(0,0,0,0.5)]">
                                    <span className="absolute inset-0 rounded-full border border-nx-lime/20" aria-hidden="true" />
                                    <item.icon size={18} strokeWidth={1.5} className="text-nx-lime" aria-hidden="true" />
                                </div>
                                <div className="flex flex-1 items-center justify-between gap-3 rounded-2xl border border-white/5 bg-nx-elev px-5 py-4">
                                    <div>
                                        <span className="text-[10px] font-extrabold text-nx-mute tabular">{String(i + 1).padStart(2, "0")}</span>
                                        <h3 className="text-sm font-extrabold uppercase tracking-[0.08em] text-nx-text">{t(item.title)}</h3>
                                    </div>
                                    <span className="shrink-0 rounded-full border border-nx-lime/30 bg-nx-lime/[0.06] px-3 py-1 text-[10px] font-bold uppercase tracking-[0.12em] text-nx-lime">
                                        {t("Coming Soon")}
                                    </span>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
